import React, { useState, useRef, useEffect } from "react";
import { FiHelpCircle, FiMail, FiPhone } from "react-icons/fi";
import {
  MAILTO_INFO_EMAIL,
  INFO_EMAIL,
  PHONE_NO_AU,
  PHONE_NO_NZ,
  CALL_NO_AU,
  CALL_NO_NZ
} from "../../utils/constants/appConstants";

/**
 * Quiet floating help control for the auth pages — a small pill in the corner
 * that reveals a support popover on hover or click (email + phone). Keeps help
 * available without a header. Popover styling ported from the nav-redesign.
 */
const AuthHelp = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  // Only open on hover for devices with a real pointer. On touch, hover events
  // fire alongside the tap and fight the click toggle — which was the mobile
  // "glitch". There, click alone drives it.
  const [canHover] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // This listener is on the document, so Escape reaches it whenever the
      // popover is open — including while the reader is working somewhere else
      // on the page. Take focus back only when it is inside the popover about
      // to unmount, which would otherwise drop it on <body> and send the next
      // Tab to the top of the document. Pulling focus in any other case would
      // be worse than the problem it solves.
      const focusWasInside =
        ref.current && ref.current.contains(document.activeElement);
      setOpen(false);
      if (focusWasInside && btnRef.current) btnRef.current.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      className="auth-help"
      ref={ref}
      data-open={open}
      onMouseEnter={canHover ? () => setOpen(true) : undefined}
      onMouseLeave={canHover ? () => setOpen(false) : undefined}
    >
      <button
        type="button"
        ref={btnRef}
        className="auth-help-btn"
        onClick={(e) => {
          // On a device where hover opens the popover, the pointer has already
          // opened it by the time a click lands — so a pointer click opens
          // rather than toggles, or it would shut what hover just revealed.
          // Leaving the pill, Escape and an outside click still close it.
          // Keyboard activation reports `detail === 0` and has no hover to
          // fight with, so it keeps the toggle.
          const fromPointer = e.detail > 0;
          setOpen((o) => (canHover && fromPointer ? true : !o));
        }}
        aria-label="Help and support"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <FiHelpCircle className="auth-help-btn-icon" aria-hidden="true" />
        <span className="auth-help-btn-text">Need help?</span>
      </button>
      {/* The popover follows the trigger so its links are what a keyboard user
          reaches on the next Tab. It is absolutely positioned, so sitting after
          the button costs nothing visually. */}
      {open && (
        <div className="auth-help-menu" role="menu">
          <p className="auth-help-title">Need a hand?</p>
          <a
            href={MAILTO_INFO_EMAIL}
            className="auth-help-item"
            role="menuitem"
          >
            <span className="auth-help-disc">
              <FiMail aria-hidden="true" />
            </span>
            <span className="auth-help-item-text">
              <span className="auth-help-item-label">Email us</span>
              <span className="auth-help-item-value">{INFO_EMAIL}</span>
            </span>
          </a>
          <a href={PHONE_NO_AU} className="auth-help-item" role="menuitem">
            <span className="auth-help-disc">
              <FiPhone aria-hidden="true" />
            </span>
            <span className="auth-help-item-text">
              <span className="auth-help-item-label">Call AU</span>
              <span className="auth-help-item-value">{CALL_NO_AU}</span>
            </span>
          </a>
          <a href={PHONE_NO_NZ} className="auth-help-item" role="menuitem">
            <span className="auth-help-disc">
              <FiPhone aria-hidden="true" />
            </span>
            <span className="auth-help-item-text">
              <span className="auth-help-item-label">Call NZ</span>
              <span className="auth-help-item-value">{CALL_NO_NZ}</span>
            </span>
          </a>
        </div>
      )}
    </div>
  );
};

export default AuthHelp;
